import { Alert, AlertTitle, Box, Button, InputAdornment, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import React, { useState, useCallback } from "react";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import RichTextEditor from "../util/textEditor/RichTextEditor";

interface IProps {
  product: Product;
  applicationId: string;
  store?: RootStore;
}

const EditShopifyListing: React.FC<IProps> = ({ product, applicationId, store }) => {
  const [title, setTitle] = useState(product.title);
  const [body, setBody] = useState(product.bodyHTML);
  const [map, setMap] = useState(product.map?.toString());
  const [msrp, setMsrp] = useState(product.msrp?.toString());
  const [loading, setLoading] = useState(false);
  const [voiceoverCaption, setVoiceoverCaption] = useState(product.voiceoverCaption);
  const [sceneName, setSceneName] = useState(product.sceneName || "Randmar");

  const { mediaStore: { scenes } } = store!;

  const saveInformation = useCallback(async (propName: string) => {
    const { productStore, snackStore } = store!;
    let success = true;

    setLoading(true);

    if (propName === "Title") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "Title", title!)
      if (!res) success = false;
    }
    if (propName === "BodyHTML") {
      const res = await productStore.setProductBodyProperty(applicationId, product.randmarSKU!, "BodyHTML", body!)
      if (!res) success = false;
    }
    if (propName === "MAP") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "MAP", map!)
      if (!res) success = false;
    }
    if (propName === "MSRP") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "MSRP", msrp!)
      if (!res) success = false;
    }
    if (propName === "VoiceoverCaption") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "VoiceoverCaption", voiceoverCaption!)
      if (!res) success = false;
    }
    if (propName === "SceneName") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "SceneName", sceneName!)
      if (!res) success = false;
    }

    if (success) snackStore.showSuccess("Information updated");
    else snackStore.showError("Could not update information");

    productStore.getProduct(applicationId, product.randmarSKU!);

    setLoading(false);
  }, [applicationId, product.randmarSKU, store, title, body, map, msrp, voiceoverCaption, sceneName]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle color="#3b6cca">Why Shopify?</AlertTitle>
        Filling out these fields will enable our resellers with Shopify websites to add this product on their website through a simple click on their Randmar Reseller Portal. As a result, keeping the Online Title, MAP Pricing, Online Description, UPC Code and product image will automatically update on all our resellers websites within seconds.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Title"
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  color="primary"
                  variant="contained"
                  children="Update"
                  loading={loading}
                  onClick={() => saveInformation("Title")}
                  disabled={product?.title === title}
                />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="MAP"
          type="number"
          value={map}
          onChange={e => setMap(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  color="primary"
                  variant="contained"
                  children="Update"
                  loading={loading}
                  onClick={() => saveInformation("MAP")}
                  disabled={product?.map?.toString() === map}
                />
              </InputAdornment>
            ),
          }}
        /><TextField
          label="MSRP"
          type="number"
          value={msrp}
          onChange={e => setMsrp(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  color="primary"
                  variant="contained"
                  children="Update"
                  loading={loading}
                  onClick={() => saveInformation("MSRP")}
                  disabled={product?.msrp?.toString() === msrp}
                />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Typography sx={{ mb: 1 / 2, fontWeight: 500, mt: 2 }}>Voiceover Caption</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          fullWidth
          multiline
          type="text"
          value={voiceoverCaption}
          onChange={e => setVoiceoverCaption(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  color="primary"
                  variant="contained"
                  children="Update"
                  loading={loading}
                  onClick={() => saveInformation("VoiceoverCaption")}
                  disabled={product?.voiceoverCaption === voiceoverCaption}
                />
              </InputAdornment>
            ),
          }}
        />

        <Select
          style={{ minWidth: "240px" }}
          value={sceneName}
          onChange={e => setSceneName(e.target.value)}
        >
          {scenes?.slice()
            .map((row) => <MenuItem key={row.name} value={row.name}>{row.name}</MenuItem>)
          }
        </Select>
        <Button
          color="primary"
          variant="contained"
          children="Update"
          loading={loading}
          onClick={() => saveInformation("SceneName")}
          disabled={product?.sceneName === sceneName}
        />
      </Stack>

      <Typography sx={{ mb: 1 / 2, fontWeight: 500, mt: 2 }}>Description</Typography>
      <RichTextEditor
        initialContent={product?.bodyHTML || ""}
        setContent={(newValue: string) => setBody(newValue)}
      />
      {/* <RichTextEditor setValue={(newValue: string) => this.setState({ body: newValue })} initialValue={product?.bodyHTML || ""} /> */}
      <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
        <Button
          color="primary"
          variant="contained"
          children="Save Description"
          loading={loading}
          disabled={product?.bodyHTML === body}
          onClick={() => saveInformation("BodyHTML")}
        />
      </Box>
    </Box>
  );
};

export default inject("store")(observer(EditShopifyListing));
